(() => {
  const theme = window.theme || { routes: {}, strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const format = (text, values) => Object.keys(values).reduce((out, key) => out.split(`[${key}]`).join(values[key]), text || '');

  /* Header border once the page leaves the top */
  const initHeader = () => {
    const sentinel = document.getElementById('TopSentinel');
    const header = document.querySelector('[data-header]');
    if (!sentinel || !header) return;
    new IntersectionObserver(([entry]) => header.classList.toggle('is-scrolled', !entry.isIntersecting)).observe(sentinel);
  };

  /* Key sections reveal once as they enter the screen */
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      revealObserver.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
  const initReveal = (root = document) => root.querySelectorAll('.reveal:not(.is-in)').forEach((el) => revealObserver.observe(el));

  /* Process: the step in the middle of the screen picks the sticky photo */
  const initProcess = (root = document) => {
    root.querySelectorAll('[data-process]').forEach((section) => {
      const images = [...section.querySelectorAll('[data-process-image]')];
      const steps = [...section.querySelectorAll('[data-step]')];
      if (!images.length) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(entry.target.dataset.step);
          images.forEach((img) => img.classList.toggle('is-active', Number(img.dataset.processImage) === index));
        });
      }, { rootMargin: '-45% 0px -45% 0px' });
      steps.forEach((step) => observer.observe(step));
    });
  };

  /* Mobile menu drawer (native dialog handles focus and Escape) */
  const initDrawer = () => {
    const drawer = document.getElementById('MenuDrawer');
    const openButton = document.querySelector('[data-drawer-open]');
    if (!drawer || !openButton) return;
    openButton.addEventListener('click', () => {
      drawer.showModal();
      openButton.setAttribute('aria-expanded', 'true');
    });
    drawer.addEventListener('close', () => openButton.setAttribute('aria-expanded', 'false'));
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer || event.target.closest('[data-drawer-close]') || event.target.closest('nav a')) drawer.close();
    });
  };

  /* Toast */
  let toastTimer;
  const showToast = (message, { icon = 'IconCheck', link } = {}) => {
    const toast = document.getElementById('Toast');
    if (!toast) return;
    toast.replaceChildren();
    const iconTemplate = document.getElementById(icon);
    if (iconTemplate) toast.append(iconTemplate.content.cloneNode(true));
    const text = document.createElement('span');
    text.textContent = message;
    toast.append(text);
    if (link) {
      const a = document.createElement('a');
      a.href = link.href;
      a.textContent = link.label;
      toast.append(a);
    }
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3600);
  };

  /* Cart count in the header */
  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.classList.toggle('has-items', count > 0);
      badge.classList.remove('bump');
      void badge.offsetWidth;
      if (count > 0) badge.classList.add('bump');
    });
    document.querySelectorAll('[data-cart-link]').forEach((link) => {
      const label = count === 1 ? theme.strings.cartCountOne : theme.strings.cartCountOther;
      link.setAttribute('aria-label', format(label, { count }));
    });
  };

  /* Add to cart without leaving the page. Falls back to a normal form post without JS. */
  const initProductForms = () => {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-product-form]');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      const label = button && button.querySelector('[data-label]');
      if (!button || button.getAttribute('aria-busy') === 'true') return;

      const original = label ? label.textContent : '';
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = theme.strings.adding;

      try {
        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || theme.strings.addError);

        const cart = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        updateCartCount(cart.item_count);

        button.classList.add('is-added');
        button.querySelectorAll('[data-icon-idle]').forEach((el) => { el.hidden = true; });
        button.querySelectorAll('[data-icon-done]').forEach((el) => { el.hidden = false; });
        if (label) label.textContent = theme.strings.added;
        showToast(format(theme.strings.addedToast, { title: form.dataset.productTitle || data.product_title || '' }), {
          link: { href: theme.routes.cart, label: theme.strings.viewCart },
        });
        setTimeout(() => {
          button.classList.remove('is-added');
          button.querySelectorAll('[data-icon-idle]').forEach((el) => { el.hidden = false; });
          button.querySelectorAll('[data-icon-done]').forEach((el) => { el.hidden = true; });
          if (label) label.textContent = original;
        }, 2400);
      } catch (error) {
        if (label) label.textContent = original;
        showToast(error.message || theme.strings.addError, { icon: 'IconWarning' });
      } finally {
        button.removeAttribute('aria-busy');
      }
    });
  };

  /* Product gallery: swipeable track, thumbnails and a full-size lightbox */
  const initGallery = (root = document) => {
    root.querySelectorAll('[data-gallery]').forEach((gallery) => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const track = gallery.querySelector('[data-gallery-track]');
      const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
      const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
      const lightbox = gallery.querySelector('[data-lightbox]');
      const lightTrack = lightbox && lightbox.querySelector('[data-lightbox-track]');
      const counter = lightbox && lightbox.querySelector('[data-lightbox-counter]');
      const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');
      const count = slides.length;

      const setCurrent = (index) => {
        thumbs.forEach((thumb, n) => thumb.setAttribute('aria-current', n === index ? 'true' : 'false'));
      };
      const scrollTrackTo = (el, index, instant) => {
        el.scrollTo({ left: el.clientWidth * index, behavior: instant ? 'auto' : behavior() });
      };
      const currentIndex = (el) => Math.round(el.scrollLeft / Math.max(el.clientWidth, 1));

      if (track) {
        const slideObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setCurrent(slides.indexOf(entry.target));
          });
        }, { root: track, threshold: 0.6 });
        slides.forEach((slide) => slideObserver.observe(slide));
      }

      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => {
          scrollTrackTo(track, index);
          setCurrent(index);
        });
      });

      if (!lightbox || !lightTrack) return;
      let lightIndex = 0;
      const updateCounter = () => {
        if (!lightbox.open) return;
        lightIndex = currentIndex(lightTrack);
        if (counter) counter.textContent = format(theme.strings.imageCount, { index: lightIndex + 1, count });
      };
      lightTrack.addEventListener('scroll', () => window.requestAnimationFrame(updateCounter), { passive: true });

      slides.forEach((slide, index) => {
        slide.querySelector('[data-lightbox-open]')?.addEventListener('click', () => {
          lightbox.showModal();
          scrollTrackTo(lightTrack, index, true);
          updateCounter();
        });
      });

      const step = (dir) => {
        const next = Math.min(Math.max(currentIndex(lightTrack) + dir, 0), count - 1);
        scrollTrackTo(lightTrack, next);
      };
      lightbox.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => step(-1));
      lightbox.querySelector('[data-lightbox-next]')?.addEventListener('click', () => step(1));
      lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); step(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); step(1); }
      });
      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox || event.target.closest('[data-lightbox-close]')) lightbox.close();
      });
      lightbox.addEventListener('close', () => {
        if (track) scrollTrackTo(track, lightIndex, true);
        setCurrent(lightIndex);
      });
    });
  };

  /* Variant select keeps the price and button in sync */
  const initVariantPickers = (root = document) => {
    root.querySelectorAll('[data-variant-picker]').forEach((select) => {
      const form = select.closest('form');
      const data = document.getElementById(select.dataset.variantData);
      if (!form || !data) return;
      const variants = JSON.parse(data.textContent);
      select.addEventListener('change', () => {
        const variant = variants.find((v) => String(v.id) === select.value);
        if (!variant) return;
        const price = form.closest('[data-product]')?.querySelector('[data-price]');
        if (price) price.textContent = variant.price_formatted;
        const button = form.querySelector('[type="submit"]');
        const label = button && button.querySelector('[data-label]');
        if (button) button.disabled = !variant.available;
        if (label) label.textContent = variant.available ? theme.strings.addToCart : variant.unavailable_label;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      });
    });
  };

  /* Selects and quantity fields that submit their form on change */
  const initAutoSubmit = () => {
    document.addEventListener('change', (event) => {
      const field = event.target.closest('[data-autosubmit]');
      if (!field || !field.form) return;
      clearTimeout(field._submitTimer);
      field._submitTimer = setTimeout(() => field.form.requestSubmit(), 350);
    });
  };

  const init = () => {
    initHeader();
    initReveal();
    initProcess();
    initDrawer();
    initProductForms();
    initGallery();
    initVariantPickers();
    initAutoSubmit();
  };

  document.addEventListener('shopify:section:load', (event) => {
    initReveal(event.target);
    initProcess(event.target);
    initGallery(event.target);
    initVariantPickers(event.target);
    event.target.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-in'));
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
